import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Minus, Maximize2, X } from "lucide-react";
import { usePopupStackContainer } from "../../context/PopupStackContext";

const PopupShell = styled.div`
  position: relative;
  width: ${(p) => (p.$minimized ? "auto" : "320px")};
  max-width: ${(p) => (p.$minimized ? "min(280px, calc(100vw - 32px))" : "320px")};
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: ${(p) => (p.$minimized ? "999px" : "12px")};
  box-shadow: ${(p) =>
    p.$minimized
      ? "0 4px 20px rgba(0, 0, 0, 0.18)"
      : "0 16px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.04)"};
  overflow: hidden;
  user-select: none;
  pointer-events: auto;
  transition: border-radius 0.2s ease, box-shadow 0.2s ease;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: ${(p) => (p.$minimized ? "0.4rem 0.65rem" : "0.5rem 0.6rem")};
  background: #f9fafb;
  border-bottom: ${(p) => (p.$minimized ? "none" : "1px solid #e5e7eb")};
  cursor: ${(p) => (p.$minimized ? "pointer" : "default")};

  &:focus {
    outline: none;
  }
`;

const HeaderTitle = styled.span`
  flex: 1;
  font-size: 0.8rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const MinimizedProgressTrack = styled.div`
  height: 3px;
  background: #e5e7eb;
  width: 100%;
`;

const MinimizedProgressFill = styled.div`
  height: 100%;
  width: ${(p) => p.$width}%;
  background: ${(p) =>
    p.$failed ? "#ef4444" : p.$complete ? "#10b981" : "#3b82f6"};
  transition: width 0.25s ease;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;

  svg {
    stroke: currentColor;
    flex-shrink: 0;
  }

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

const PopupBody = styled.div`
  padding: 0.75rem;
`;

const JobName = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const JobMeta = styled.div`
  font-size: 0.72rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const ProgressTrack = styled.div`
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(p) => p.$width}%;
  background: ${(p) =>
    p.$failed ? "#ef4444" : p.$complete ? "#10b981" : "#3b82f6"};
  transition: width 0.25s ease;
`;

const SuccessMessage = styled.div`
  font-size: 0.72rem;
  color: #047857;
  line-height: 1.45;
`;

export default function BatchOcrProgressPopup({ progress, batchName }) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const container = usePopupStackContainer();

  // Reset dismissal whenever a fresh progress cycle starts for this batch
  useEffect(() => {
    if (progress && !progress.is_complete) {
      setDismissed(false);
    }
  }, [progress?.batch_id]);

  if (!progress || dismissed || !container) return null;

  const total = progress.ocr_total || 0;
  const completed = progress.ocr_completed || 0;
  const failed = progress.ocr_failed || 0;
  const processed = completed + failed;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isComplete = !!progress.is_complete;
  const hasFailures = failed > 0;

  const minimizedLabel = isComplete
    ? "OCR complete · 100%"
    : `Scanning cards · ${percent}%`;

  const iconProps = { size: 16, strokeWidth: 2.25, "aria-hidden": true };

  return createPortal(
    <PopupShell $minimized={isMinimized}>
      <PopupHeader
        $minimized={isMinimized}
        onClick={() => {
          if (isMinimized) setIsMinimized(false);
        }}
        role={isMinimized ? "button" : undefined}
        tabIndex={isMinimized ? 0 : undefined}
        onKeyDown={(e) => {
          if (isMinimized && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setIsMinimized(false);
          }
        }}
      >
        <HeaderTitle>
          {isMinimized ? minimizedLabel : "OCR processing"}
        </HeaderTitle>
        <IconButton
          type="button"
          title={isMinimized ? "Expand" : "Minimize to corner"}
          onClick={(e) => {
            e.stopPropagation();
            setIsMinimized((prev) => !prev);
          }}
        >
          {isMinimized ? (
            <Maximize2 {...iconProps} />
          ) : (
            <Minus {...iconProps} />
          )}
        </IconButton>
        {isComplete && (
          <IconButton
            type="button"
            title="Close"
            onClick={(e) => {
              e.stopPropagation();
              setDismissed(true);
            }}
          >
            <X {...iconProps} />
          </IconButton>
        )}
      </PopupHeader>
      {isMinimized && (
        <MinimizedProgressTrack>
          <MinimizedProgressFill
            $width={percent}
            $complete={isComplete}
            $failed={hasFailures && isComplete}
          />
        </MinimizedProgressTrack>
      )}
      {!isMinimized && (
        <PopupBody>
          <JobName>{batchName || "Batch"}</JobName>
          {isComplete ? (
            <SuccessMessage>
              {progress.message || "All OCR processing completed"}
              {hasFailures ? ` (${failed} failed)` : ""}
            </SuccessMessage>
          ) : (
            <>
              <JobMeta>
                Scanning cards… · {processed}/{total}
              </JobMeta>
              <ProgressTrack>
                <ProgressFill $width={percent} $complete={isComplete} $failed={false} />
              </ProgressTrack>
            </>
          )}
        </PopupBody>
      )}
    </PopupShell>,
    container
  );
}
